/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ActionsSlice } from '../../../../ts/state';
import * as operations from '../../../ia/store/operations';
import { revertVersion } from '../../../ia/store/publish/actions';
import { SET_SKILL_ID } from '../../../ia/store/session/actionTypes';
import localStorage from '../../../ia/util/localStorage';
import { ENTITY_TYPES, isSystemEntity } from '../../util/actionUtil';
import * as boostActions from '../boosts/actions';
import { deleteDataType } from '../data-types/actions';
import * as modelActionTypes from '../models/actions/actionTypes';
import {
  addResponseFromVariableResponse,
  setCollectVerbatimOnStep,
  setPrivacyOnStep,
  setSlotExamplesOnStep,
  updateAllowTopicSwitch,
  updateStepDynamicOptions
} from './actions';
import * as actionTypes from './actionTypes';
import { createActionsFromTemplate } from './ducks/create';

const initialState: ActionsSlice = {
  actionId: null,
  actionsLoaded: false, // true when actions data has been loaded once
  autoSave: true,
  boostsModified: false,
  currentVarEntity: '', //The currently select variable's corresponding entity
  creatingTemplateAction: false, //true when TemplatePreviewModal should have loading spinner
  dataTypes: { create: [], update: [] },
  entities: { create: [], delete: [], update: [], updateFully: [] },
  entityValueUpdatesToTrack: {},
  errorMessage: null,
  exampleEdited: false,
  isDirty: false,
  skillId: null,
  launchMode: undefined,
  listFetched: null, // timestamp for last listAction() call
  listLoading: false,
  loadingAction: false, //true when the current editor should have a loading/saving indicator
  loadingActions: false, //true when the actions list (datatable) should have loading indicator
  modifiedStepIds: [],
  removedStepVariableIds: [],
  createdStepVariableIds: [],
  duplicatedButNotModifiedStepIds: [],
  stepId: null,
  variables: [],
  intentsActionsMap: {},
  editorContext: 'Editor',
  showConvoActionPanel: {
    showConvoPanel: false,
    returnRoute: '',
    actionId: ''
  },
  isAIGuidedAction: false,
  aiGuided: {
    model_id: 'ibm/granite-13b-chat-v2',
    content: '',
    prompt_instruction: ''
  }
};

export const computeState = (updates?: any): any => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const addSysNumberAsSupportingEntity = oldState => {
  const state = { ...oldState, isDirty: true };
  //the user has added a sys-percentage or sys-currency to the step, we also need
  //to automatically add sys-number to the skill so that when the user (Cade) enters
  //'50' instead of '50%' or 'fifty five' instead of 'fifty-five dollars' that the numeric value entered
  //is detected
  state.entities = {
    ...state.entities,
    create: _.uniq([...state.entities.create].concat(ENTITY_TYPES.number.id)), //add to array of entities to create
    delete: _.pull([...state.entities.delete], ENTITY_TYPES.number.id), //entity should be created, don't try to delete
    update: _.pull([...state.entities.update], ENTITY_TYPES.number.id) //don't think this is possible, but let's be sure
  };

  return state;
};

const defineCustomerResponse = (oldState, entityId, entityExists, stepId) => {
  //this reducer is only concerned about tracking which entities must be created when the action is saved
  //it will make sure the provided entity is not in the list of entities to update/delete
  const state = {
    ...oldState,
    isDirty: true,
    modifiedStepIds: oldState.modifiedStepIds?.includes(stepId)
      ? oldState.modifiedStepIds
      : _.uniq([...oldState.modifiedStepIds, stepId]),
    duplicatedButNotModifiedStepIds:
      oldState.duplicatedButNotModifiedStepIds.filter(id => id !== stepId)
  };

  if (entityExists) {
    return state;
  }

  //the user has added an entity (options) to a step
  //we want to make sure we don't somehow have this in the
  //list of entities to delete. The latest operation is to create
  //so we must make sure it is not deleted due to a prior operation
  state.entities = {
    ...state.entities,
    create: _.uniq([...state.entities.create].concat(entityId)), //add to array of entities to create
    delete: _.pull([...state.entities.delete], entityId), //entity should be created, don't try to delete
    update: _.pull([...state.entities.update], entityId) //don't think this is possible, but let's be sure
  };
  return state;
};

const duplicateStepEntity = (oldState, entityId) => {
  const state = { ...oldState };
  state.entities = {
    ...state.entities,
    create: _.uniq([...state.entities.create].concat(entityId)), //add to array of entities to create
    delete: _.pull([...state.entities.delete], entityId), //entity should be created, don't try to delete
    update: _.pull([...state.entities.update], entityId) //don't think this is possible, but let's be sure
  };
  return state;
};

const updateStepOptions = (
  oldState,
  entityId,
  dataTypeOperation,
  dataTypeId,
  valuesToCreate = [],
  valuesToDelete = [],
  valuesToUpdate = []
) => {
  //this reducer is only concerned with tracking which entities need to be updated when the user saves an action
  //the entity provided to this reducer will be removed from the list of entities to be deleted (it it was present there)
  const state = {
    ...oldState,
    isDirty: true,
    duplicatedButNotModifiedStepIds:
      oldState.duplicatedButNotModifiedStepIds.filter(
        id => id !== oldState.stepId
      )
  };
  //the user has updated an existing entity (options)
  //we want to make sure we don't somehow have this in the
  //list of entities to delete. The latest operation is to update
  //so we must make sure it is not deleted due to a prior operation
  state.entities = {
    ...state.entities,
    create: [...state.entities.create], //entity may be in array of entities to create, in which case leave there
    delete: _.pull([...state.entities.delete], entityId), //entity should be updated, don't try to delete
    update: [...state.entities.update]
  };
  if (state.entities.create.indexOf(entityId) === -1) {
    //it is not in the create array so we need to add it to the 'update' array
    state.entities.update = _.uniq(state.entities.update.concat(entityId)); //add it to update array
  }
  if (dataTypeId) {
    state.dataTypes = {
      create: [...state.dataTypes.create],
      update: [...state.dataTypes.update]
    };
    if (dataTypeOperation === operations.CREATE) {
      state.dataTypes.create = _.uniq(
        state.dataTypes.create.concat(dataTypeId)
      );
    } else if (dataTypeOperation === operations.UPDATE) {
      state.dataTypes.update = _.uniq(
        state.dataTypes.update.concat(dataTypeId)
      );
    }
  }
  if (valuesToCreate.length || valuesToDelete.length || valuesToUpdate.length) {
    state.entityValueUpdatesToTrack = { ...state.entityValueUpdatesToTrack };
    if (!state.entityValueUpdatesToTrack[entityId]) {
      state.entityValueUpdatesToTrack[entityId] = {
        create: [],
        delete: [],
        update: []
      };
    }
    const tracker = _.cloneDeep(state.entityValueUpdatesToTrack[entityId]);
    if (valuesToCreate.length) {
      valuesToCreate.forEach(value => {
        tracker.delete = _.pull(tracker.delete, value);
        if (tracker.delete.indexOf(value) !== -1) {
          tracker.create = _.pull(tracker.create, value);
          tracker.update = _.uniq(tracker.update.concat(value));
        } else {
          tracker.update = _.pull(tracker.update, value);
          tracker.create = _.uniq(tracker.create.concat(value));
        }
      });
    }
    if (valuesToUpdate.length) {
      valuesToUpdate.forEach(value => {
        tracker.delete = _.pull(tracker.delete, value);
        if (tracker.create.indexOf(value) === -1) {
          tracker.update = _.uniq(tracker.update.concat(value));
        }
      });
    }
    if (valuesToDelete.length) {
      valuesToDelete.forEach(value => {
        tracker.update = _.pull(tracker.update, value);
        if (tracker.create.indexOf(value) === -1) {
          //the user created and deleted the value without saving, we should _just not save_ the value
          tracker.delete = _.uniq(tracker.delete.concat(value));
        }
        tracker.create = _.pull(tracker.create, value);
      });
    }
    state.entityValueUpdatesToTrack[entityId] = tracker;
  }
  return state;
};

const removeCustomerResponse = (
  oldState,
  entityId,
  entityReferenced,
  dataTypeId?,
  stepId?
) => {
  const state = {
    ...oldState,
    isDirty: true,
    modifiedStepIds: oldState.modifiedStepIds?.includes(stepId)
      ? oldState.modifiedStepIds
      : _.uniq([...oldState.modifiedStepIds, stepId]),
    duplicatedButNotModifiedStepIds:
      oldState.duplicatedButNotModifiedStepIds.filter(id => id !== stepId)
  };

  if (entityReferenced) {
    return state;
  }

  if (dataTypeId) {
    if (state.dataTypes.create.indexOf(dataTypeId) !== -1) {
      //we were trying to 'create' this data_type and it is not used anywhere else
      //no need to make an API call to delete this entity
      state.dataTypes = {
        ...state.dataTypes,
        create: _.pull([...state.dataTypes.create], dataTypeId), //entity should be deleted don't try to create
        update: _.pull([...state.dataTypes.update], dataTypeId) //entity should be deleted don't try to update
      };
    }
    if (state.entities.create.indexOf(entityId) !== -1) {
      //we were trying to 'create' this entity (meaning it was newly created along with the data type,
      //no need to make an API call to delete this entity
      state.entities = {
        ...state.entities,
        delete: [...state.entities.delete],
        create: _.pull([...state.entities.create], entityId), //entity should be deleted don't try to create
        update: _.pull([...state.entities.update], entityId) //entity should be deleted don't try to update
      };
    }
  } else if (entityId) {
    if (state.entities.create.indexOf(entityId) !== -1) {
      //we were trying to 'create' this entity (meaning it was newly created,
      //no need to make an API call to delete this entity
      state.entities = {
        ...state.entities,
        delete: [...state.entities.delete],
        create: _.pull([...state.entities.create], entityId), //entity should be deleted don't try to create
        update: _.pull([...state.entities.update], entityId) //entity should be deleted don't try to update
      };
    } else if (!isSystemEntity(entityId)) {
      // we should not delete system entity
      // entity already exists in store, add to list of items to delete
      state.entities = {
        ...state.entities,
        delete: _.uniq([...state.entities.delete].concat(entityId)),
        create: _.pull([...state.entities.create], entityId), //entity should be deleted don't try to create
        update: _.pull([...state.entities.update], entityId) //entity should be deleted don't try to update
      };
    }
    if (state.entityValueUpdatesToTrack[entityId]) {
      state.entityValueUpdatesToTrack = { ...state.entityValueUpdatesToTrack };
      delete state.entityValueUpdatesToTrack[entityId];
    }
  }

  return state;
};

export const reducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case boostActions.addBoosts.toString():
    case boostActions.removeBoost.toString():
      return {
        ...state,
        boostsModified: true
      };
    case actionTypes.SET_EDITOR_CONTEXT:
      return { ...state, editorContext: action.editorContext };
    case actionTypes.SET_CURRENT_ACTION_ID:
      return { ...state, actionId: action.actionId };
    case actionTypes.OPEN_PREVIEW_DEBUG_MODE:
      return {
        ...state,
        showPreviewDebugMode: true
      };
    case actionTypes.CLOSE_PREVIEW_DEBUG_MODE:
      return {
        ...state,
        showPreviewDebugMode: false
      };
    case actionTypes.UPDATE_AUTO_SAVE:
      localStorage.set('ACTIONS_AUTO_SAVE', action.enabled);
      return { ...state, autoSave: action.enabled };
    case actionTypes.CREATE_EMPTY_ACTION:
      return {
        ...state,
        actionId: action.actionId,
        launchMode: action.launchMode,
        entityValueUpdatesToTrack: {},
        exampleEdited: true, //set this to true such that an intent will be created onSave
        modifiedStepIds: _.uniq([...state.modifiedStepIds, action.variableId]),
        stepId: null,
        isDirty: false
      };
    case modelActionTypes.STEP_CREATE: {
      const newState = { ...state };
      newState.stepId = action.options?.select ? action.stepId : state.stepId;
      newState.modifiedStepIds = _.uniq([
        ...newState.modifiedStepIds,
        action.stepId
      ]);

      newState.createdStepVariableIds = [...state.createdStepVariableIds];
      if (!state.createdStepVariableIds.includes(action.stepId)) {
        newState.createdStepVariableIds.push(action.stepId);
      }
      newState.isDirty = true;
      if (action.options?.duplicateStep) {
        newState.duplicatedButNotModifiedStepIds = [
          ...state.duplicatedButNotModifiedStepIds,
          action.stepId
        ];
      }
      return newState;
    }
    case actionTypes.ADD_SYS_NUMBER_AS_SUPPORTING_ENTITY:
      return addSysNumberAsSupportingEntity(state);
    case modelActionTypes.STEP_DEFINE_CUSTOMER_RESPONSE:
      return defineCustomerResponse(
        state,
        action.entityId,
        action.entityExists,
        action.stepId
      );
    case actionTypes.DUPLICATE_STEP_ENTITY:
      return duplicateStepEntity(state, action.entityId);
    case modelActionTypes.STEP_REMOVE_CUSTOMER_RESPONSE:
      return removeCustomerResponse(
        state,
        action.entityId,
        action.entityReferenced,
        action.dataTypeId,
        action.stepId
      );

    case modelActionTypes.STEP_DELETE: {
      //if the step has an entity we also want to delete that entity when we save.
      const newState = !action.entityId
        ? { ...state }
        : removeCustomerResponse(
            state,
            action.entityId,
            isSystemEntity(action.entityId)
          );
      const variablesToDelete = [];
      if (state.createdStepVariableIds.includes(action.variableId)) {
        newState.createdStepVariableIds =
          newState.createdStepVariableIds.filter(v => v !== action.variableId);
      } else {
        variablesToDelete.push(action.variableId);
      }
      if (action.resultVariableId) {
        variablesToDelete.push(action.resultVariableId);
      }
      newState.stepId =
        newState.stepId === action.stepId ? action.nextStepId : newState.stepId; //update selected step
      newState.isDirty = true;
      newState.removedStepVariableIds = [
        ...newState.removedStepVariableIds,
        ...variablesToDelete
      ];
      newState.duplicatedButNotModifiedStepIds =
        state.duplicatedButNotModifiedStepIds.filter(
          id => id !== action.stepId
        );
      return newState;
    }

    case actionTypes.SELECT_STEP:
      return { ...state, stepId: action.stepId };

    case modelActionTypes.STEP_UPDATE_OPTIONS:
      return updateStepOptions(
        state,
        action.entityId,
        action.dataTypeOperation,
        action.dataTypeId,
        action.valuesToCreate,
        action.valuesToDelete,
        action.valuesToUpdate
      );

    case modelActionTypes.ACTIONS_SET_TITLE:
    case modelActionTypes.ACTIONS_ADD_DISPLAY_NAME:
      return {
        ...state,
        isDirty: true
      };
    case modelActionTypes.STEP_REORDER:
    case modelActionTypes.STEP_UPDATE_CONDITION:
    case modelActionTypes.STEP_UPDATE_CONTEXT:
    case modelActionTypes.STEP_SET_TITLE:
    case modelActionTypes.STEP_UPDATE_VALIDATION:
    case modelActionTypes.STEP_UPDATE_RESPONSE_COLLECTION_BEHAVIOR:
      return {
        ...state,
        isDirty: true,
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.stepId
          )
      };

    case modelActionTypes.STEP_UPDATE_END_BEHAVIOR:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([...state.modifiedStepIds, action.stepId]),
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.stepId
          ),
        removedStepVariableIds: action.deletedResultVariable
          ? _.uniq(
              state.removedStepVariableIds.concat([
                action.deletedResultVariable
              ])
            )
          : state.removedStepVariableIds
      };

    case modelActionTypes.STEP_SET_FREE_TEXT_RESPONSE:
    case modelActionTypes.STEP_UPDATE_OUTPUT:
    case modelActionTypes.STEP_SET_TEXT:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([...state.modifiedStepIds, action.stepId]),
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.stepId
          )
      };
    case actionTypes.SET_DANGER_WORD_VALUES:
      return {
        ...state,
        isDirty: true,
        entities: {
          ...state.entities,
          updateFully: _.uniq([...state.entities.updateFully, 'danger_words'])
        }
      };
    case actionTypes.SET_PROFANE_WORD_VALUES:
      return {
        ...state,
        isDirty: true,
        entities: {
          ...state.entities,
          updateFully: _.uniq([...state.entities.updateFully, 'profane_words'])
        }
      };
    case actionTypes.SET_STEP_MAX_HITS:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([...state.modifiedStepIds, action.stepId])
      };
    case actionTypes.ADD_LAUNCH_MODE:
      return {
        ...state,
        launchMode: action.launchMode
      };
    case actionTypes.UPDATE_CURRENT_VAR_ENTITY:
      return { ...state, currentVarEntity: action.currentVarEntity };
    case actionTypes.LIST_ACTIONS_START:
    case actionTypes.DELETE_ACTION_START:
      return {
        ...state,
        loadingActions: true,
        errorMessage: null
      };
    case actionTypes.UPDATE_ACTION_DISAMBIGUATION_START:
    case actionTypes.RENAME_ACTION_START:
      return {
        ...state,
        errorMessage: null
      };
    case modelActionTypes.ACTIONS_GET_START:
      return {
        ...state,
        actionId: null,
        loadingAction: true,
        errorMessage: null,
        isDirty: false
      };
    case actionTypes.CLEAR_ACTION_START:
      return {
        ...state,
        actionId: null,
        listLoading: true,
        loadingAction: true,
        errorMessage: null,
        isDirty: false,
        launchMode: null,
        isAIGuidedAction: false,
        aiGuided: {
          model_id: 'ibm/granite-13b-chat-v2',
          content: '',
          prompt_instruction: ''
        }
      };
    case actionTypes.SAVE_CURRENT_ACTION_START:
      return { ...state, isDirty: false, loadingAction: true };
    case actionTypes.SAVE_CURRENT_ACTION_CONFLICT_ERROR:
      return {
        ...state,
        loadingAction: false,
        loadingActions: false,
        errorMessage: action.error.error,
        saveConflictError: true
      };
    case actionTypes.CLEAR_CURRENT_ACTION_CONFLICT_ERROR:
      return {
        ...state,
        saveConflictError: false
      };
    case createActionsFromTemplate.pending.toString():
      return {
        ...state,
        creatingTemplateAction: true,
        errorMessage: null
      };
    case actionTypes.GET_ACTION_SUCCESS:
      return {
        ...state,
        actionId: action.actionId,
        boostsModified: false,
        stepId: null,
        dataTypes: { create: [], update: [] },
        entities: { create: [], delete: [], update: [], updateFully: [] },
        errorMessage: null,
        exampleEdited: false,
        loadingActions: false,
        loadingAction: false,
        isDirty: false
      };
    case actionTypes.LIST_ACTIONS_SUCCESS:
      return {
        ...state,
        // Update when we last fetched actions and skill those actions belong to
        actionsLoaded: true,
        listFetched: Date.now(),
        skillId: action.skillId,
        dataTypes: { create: [], update: [] },
        entities: { create: [], delete: [], update: [], updateFully: [] },
        errorMessage: null,
        exampleEdited: false,
        loadingActions: false,
        loadingAction: false,
        modifiedStepIds: [],
        removedStepVariableIds: [],
        createdStepVariableIds: [],
        variables: [],
        intentsActionsMap: _.transform(
          action.response?.entities?.actions,
          (result, value, key) => {
            const intent =
              value.condition?.intent || value.condition?.and?.[0]?.intent;
            if (intent) {
              result[intent] = key;
            }
          }
        )
      };
    case actionTypes.UPDATE_ACTION_DISAMBIGUATION_SUCCESS:
      return {
        ...state,
        boostsModified: false,
        dataTypes: { create: [], update: [] },
        entities: { create: [], delete: [], update: [], updateFully: [] },
        entityValueUpdatesToTrack: {},
        errorMessage: null,
        listLoading: false,
        loadingAction: false,
        loadingActions: false,
        modifiedStepIds: [],
        removedStepVariableIds: [],
        createdStepVariableIds: [],
        variables: []
      };
    case actionTypes.CLEAR_ACTION_SUCCESS:
    case actionTypes.DELETE_ACTION_SUCCESS:
    case actionTypes.RENAME_ACTION_SUCCESS:
    case modelActionTypes.ACTIONS_SAVE:
      return {
        ...state,
        boostsModified: false,
        dataTypes: { create: [], update: [] },
        entities: { create: [], delete: [], update: [], updateFully: [] },
        entityValueUpdatesToTrack: {},
        errorMessage: null,
        exampleEdited: false,
        listLoading: false,
        loadingAction: false,
        loadingActions: false,
        modifiedStepIds: [],
        removedStepVariableIds: [],
        createdStepVariableIds: [],
        variables: []
      };
    case createActionsFromTemplate.fulfilled.toString():
      return {
        ...state,
        creatingTemplateAction: false,
        errorMessage: null
      };
    case actionTypes.SAVE_CURRENT_ACTION_ERROR:
      return {
        ...state,
        isDirty: true,
        loadingActions: false,
        loadingAction: false,
        errorMessage: action.error
      };
    case actionTypes.LIST_ACTIONS_ERROR:
    case actionTypes.GET_ACTION_ERROR:
    case actionTypes.DELETE_ACTION_ERROR:
    case actionTypes.RENAME_ACTION_ERROR:
    case actionTypes.UPDATE_ACTION_DISAMBIGUATION_ERROR:
    case createActionsFromTemplate.rejected.toString():
      return {
        ...state,
        creatingTemplateAction: false,
        errorMessage: action.error
      };
    case modelActionTypes.ACTIONS_ADD_EXAMPLE:
    case modelActionTypes.ACTIONS_ADD_MULTIPLE_EXAMPLES:
    case modelActionTypes.ACTIONS_ADD_MULTIPLE_COUNTER_EXAMPLES:
    case modelActionTypes.ACTIONS_DELETE_EXAMPLE:
    case modelActionTypes.ACTIONS_UPDATE_EXAMPLE:
    case actionTypes.CREATE_COUNTER_EXAMPLE:
    case actionTypes.UPDATE_COUNTER_EXAMPLE:
    case actionTypes.DELETE_COUNTER_EXAMPLE:
      return { ...state, isDirty: true, exampleEdited: true };
    case modelActionTypes.ACTION_UPDATE_CONDITION:
    case actionTypes.UPDATE_DIRTY_STATE:
      return {
        ...state,
        isDirty: action.isDirty
      };

    case actionTypes.GLOBAL_SETTINGS_REDIRECT_START:
      return {
        ...state,
        redirectGlobalSettingsTo: action.tabName
      };
    case actionTypes.GLOBAL_SETTINGS_REDIRECT_SUCCESS:
      return {
        ...state,
        redirectGlobalSettingsTo: undefined
      };
    // Clear list cache if a data type is deleted, we need to force it fetch actions again to get the validation info
    case deleteDataType.fulfilled.toString():
      return {
        ...state,
        listFetched: null
      };
    // Clear cache when a new skill id is set or the action is unloaded
    case SET_SKILL_ID:
    case actionTypes.RESET_ACTIONS_STATE:
    case revertVersion.fulfilled:
      return {
        ...state,
        listFetched: null,
        skillId: null
      };
    case addResponseFromVariableResponse.type:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([...state.modifiedStepIds, action.payload]),
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.payload
          )
      };
    case updateStepDynamicOptions.type:
    case updateAllowTopicSwitch.type:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([
          ...state.modifiedStepIds,
          action.payload.stepId
        ]),
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.payload.stepId
          )
      };
    case setPrivacyOnStep.type:
    case setCollectVerbatimOnStep.type:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([
          ...state.modifiedStepIds,
          action.payload.stepId
        ]),
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.payload.stepId
          )
      };
    case setSlotExamplesOnStep.type:
      return {
        ...state,
        isDirty: true,
        modifiedStepIds: _.uniq([
          ...state.modifiedStepIds,
          action.payload.stepId
        ]),
        duplicatedButNotModifiedStepIds:
          state.duplicatedButNotModifiedStepIds.filter(
            id => id !== action.payload.stepId
          )
      };
    case actionTypes.OPEN_CONVO_ACTION_PANEL:
      return {
        ...state,
        showConvoActionPanel: {
          showConvoPanel: true,
          convoPanelReturnRoute: action.returnRoute,
          actionId: action.actionId
        }
      };
    case actionTypes.HIDE_CONVO_ACTION_PANEL:
      return {
        ...state,
        showConvoActionPanel: {
          showConvoPanel: false,
          convoPanelReturnRoute: '',
          actionId: ''
        }
      };
    case actionTypes.CLEAR_CONVO_ACTION_EDIT_ID:
      return {
        ...state,
        showConvoActionPanel: {
          ...state.showConvoActionPanel,
          actionId: ''
        }
      };
    case actionTypes.SET_KNOWLEDGE_CONTENT:
      return {
        ...state,
        isDirty: true,
        isAIGuidedAction: true,
        aiGuided: {
          ...state.aiGuided,
          content: action.knowledgeContent
        }
      };
    case actionTypes.SET_PROMPT_INSTRUCTION:
      return {
        ...state,
        isDirty: true,
        isAIGuidedAction: true,
        aiGuided: {
          ...state.aiGuided,
          prompt_instruction: action.promptInstruction
        }
      };
    case actionTypes.SET_MODEL_SELECTION:
      return {
        ...state,
        isDirty: true,
        isAIGuidedAction: true,
        aiGuided: {
          ...state.aiGuided,
          model_id: action.modelName
        }
      };
    case actionTypes.SET_AI_GUIDED:
      return {
        ...state,
        aiGuided: action.aiGuided
      };
    case actionTypes.SET_IS_AI_GUIDED_ACTION:
      return {
        ...state,
        isAIGuidedAction: true
      };
    default:
      break;
  }
  return state;
};
